import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Collapse, Grid, Modal } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ViewAndFilterButton from "../components/ViewAndFilterButton";
import ViewOption from "../components/ViewOption";
import FilterWrapper from "../components/general/FiltersWrapper";
import Loader from "../components/general/Loader";
import LoadingBackdrop from "../components/general/LoadingBackdrop";
import NoData from "../components/general/NoData";
import NunitoText from "../components/general/NunitoText";
import ReusableButton from "../components/general/ReusableButton";
import ReusableTextField from "../components/general/ReusableTextField";
import ConfirmAbsentParticipant from "../components/modal/ConfirmAbsentParticipant";
import ConfirmModal from "../components/modal/ConfirmModal";
import EditParticipantRankingModal from "../components/modal/EditParticipantRankingModal";
import HeavyTable from "../components/table/HeavyTable";
import { showWarningSwal } from "../functions/alert";
import {
  buildQueryString,
  cancelAction,
  changeViewOptions,
  controlFilter,
  controlView,
  makeSureIsArray,
} from "../functions/general";
import { enhancedGetReport, getEnhancedCompReport } from "../functions/getData";
import { patchEditParticipantRanking } from "../functions/patchData";
import { MakeAbsentParticipant, MarkParticipant } from "../functions/postData";
import { showNotification } from "../functions/snackbar";

export default function EnhancedCompReport() {
  const isMounted = useRef(false);
  const location = useLocation();
  const isMarkedManually = location.search.includes("marked_manually=1");

  const competitionHeader = [
    { id: "name", label: "participant" },
    { id: "index_no", label: "index" },
    { id: "iac_status", label: "status" },
    ...(!isMarkedManually
      ? [{ id: "answers_exists", label: "Answers Uploaded" }]
      : [{ id: "marks_uploaded", label: "Marks Uploaded" }]),
    { id: "certificate_no", label: "certificate number" },
    { id: "competition", label: "Competition" },
    { id: "organization", label: "organization" },
    { id: "country", label: "country" },
    { id: "level", label: "level" },
    { id: "grade", label: "grade" },
    { id: "school", label: "school" },
    { id: "tuition_centre", label: "tuition" },
    { id: "points", label: "points" },
    { id: "award", label: "award" },
    { id: "school_rank", label: "school rank" },
    { id: "country_rank", label: "country rank" },
    { id: "global_rank", label: "global rank" },
  ];
  document.title = "Competition Report";

  // Parse the id from URL query parameters properly
  const getCompetitionId = () => {
    const params = new URLSearchParams(location.search);
    return params.get("id");
  };
  const competitionId = getCompetitionId();

  const [header, setHeader] = useState(competitionHeader);
  const [competitionTable, setCompetitionTable] = useState();
  const getViewOptions = () => {
    const baseOptions = [
      { label: "Participant", state: true, key: "name" },
      { label: "Index", state: true, key: "index_no" },
      { label: "Status", state: true, key: "iac_status" },
      ...(!isMarkedManually
        ? [{ label: "Answers Uploaded", state: true, key: "answers_exists" }]
        : [{ label: "Marks Uploaded", state: true, key: "marks_uploaded" }]),
      { label: "Certificate Number", state: true, key: "certificate_no" },
      { label: "Competition", state: true, key: "competition" },
      { label: "Organization", state: true, key: "organization" },
      { label: "Country", state: true, key: "country" },
      { label: "Level", state: true, key: "level" },
      { label: "Grade", state: true, key: "grade" },
      { label: "School", state: true, key: "school" },
      { label: "Tuition", state: true, key: "tuition_centre" },
      { label: "Points", state: true, key: "points" },
      { label: "Award", state: true, key: "award" },
      { label: "School Rank", state: true, key: "school_rank" },
      { label: "Country Rank", state: true, key: "country_rank" },
      { label: "Global Rank", state: true, key: "global_rank" },
    ];
    return baseOptions;
  };

  const [viewOptions, setViewOptions] = useState(getViewOptions());
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [firstLoaded, setFirstLoaded] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [filtering, setFiltering] = useState(false);
  const [selected, setSelected] = useState([]);
  const [selecting, setSelecting] = useState([]);
  const [confirmMarking, setConfirmMarking] = useState(false);
  const [absentParticipant, setAbsentParticipant] = useState(false);
  const [onEditParticipantRanking, setOnEditParticipantRanking] =
    useState(false);
  const [participant, setParticipant] = useState("");
  const [sortAbleColumns, setSortableColumns] = useState([]);

  // Consolidated query parameters state
  const [queryParams, setQueryParams] = useState({
    limits: 10,
    page: 1,
    search: "",
    award: "",
    country: "",
    grade: "",
    status: "",
    orderBy: "",
    orderDirection: "",
    ...(!isMarkedManually ? { answers_uploaded: "" } : { marks_uploaded: "" }),
  });

  const handleView = () => controlView(viewing, setViewing, setFiltering);
  const handleFilter = () => controlFilter(filtering, setFiltering, setViewing);
  const cancelEditParticipantRanking = () =>
    cancelAction(setOnEditParticipantRanking);
  const cancelConfirmMarking = () => cancelAction(setConfirmMarking);
  const cancelAbsentStudent = () => cancelAction(setAbsentParticipant);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const isFirstRender = useRef(true);

  // Update query parameters
  const updateQueryParams = (updates) => {
    setQueryParams((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  // Reset query parameters to initial state
  const resetQueryParams = () => {
    setQueryParams({
      limits: 10,
      page: 1,
      search: "",
      award: "",
      country: "",
      grade: "",
      status: "",
      orderBy: "",
      orderDirection: "",
      ...(!isMarkedManually
        ? { answers_uploaded: "" }
        : { marks_uploaded: "" }),
    });
  };

  const adjustEndpoint = (endpoint, value) => {
    const currentParams = { ...queryParams };

    // Build the endpoint with current filter values
    if (value !== currentParams.award && currentParams.award) {
      endpoint += `&award=${currentParams.award}`;
    }
    if (value !== currentParams.country && currentParams.country) {
      endpoint += `&country=${currentParams.country}`;
    }
    if (value !== currentParams.grade && currentParams.grade) {
      endpoint += `&grade=${currentParams.grade}`;
    }
    if (value !== currentParams.status && currentParams.status) {
      endpoint += `&status=${currentParams.status}`;
    }

    return endpoint;
  };

  const filters = [
    {
      label: "Award",
      endpoint: `competition/report/${competitionId}?get_filter=award`,
      value: queryParams.award,
      onChange: (value) => updateQueryParams({ award: value }),
      key: "award",
      setValue: (value) => updateQueryParams({ award: value }),
    },
    {
      label: "Country",
      endpoint: `competition/report/${competitionId}?get_filter=country`,
      value: queryParams.country,
      onChange: (value) => updateQueryParams({ country: value }),
      key: "country",
      setValue: (value) => updateQueryParams({ country: value }),
    },
    {
      label: "Grade",
      endpoint: `competition/report/${competitionId}?get_filter=grade`,
      value: queryParams.grade,
      onChange: (value) => updateQueryParams({ grade: value }),
      key: "grade",
      setValue: (value) => updateQueryParams({ grade: value }),
    },
    {
      label: "Status",
      endpoint: `competition/report/${competitionId}?get_filter=status`,
      value: queryParams.status,
      onChange: (value) => updateQueryParams({ status: value }),
      key: "status",
      setValue: (value) => updateQueryParams({ status: value }),
    },
    ...(!isMarkedManually
      ? [
          {
            label: "Answers Uploaded",
            endpoint: `competition/report/${competitionId}?get_filter=answers_uploaded`,
            value: queryParams.answers_uploaded,
            onChange: (value) => updateQueryParams({ answers_uploaded: value }),
            key: "answers_uploaded",
            setValue: (value) => updateQueryParams({ answers_uploaded: value }),
          },
        ]
      : [
          {
            label: "Marks Uploaded",
            endpoint: `competition/report/${competitionId}?get_filter=marks_uploaded`,
            value: queryParams.marks_uploaded,
            onChange: (value) => updateQueryParams({ marks_uploaded: value }),
            key: "marks_uploaded",
            setValue: (value) => updateQueryParams({ marks_uploaded: value }),
          },
        ]),
  ];

  const onChangeSearch = (value) => {
    setSearch(value);
    updateQueryParams({ search: value });
  };

  const fetchData = () => {
    setSelected([]);
    setSelecting([]);
    setLoading(true);

    if (!competitionId) {
      showWarningSwal("Wrong query");
      setLoading(false);
      return;
    }

    const queryString = buildQueryString(queryParams);
    getEnhancedCompReport(
      competitionId,
      queryString,
      controller.current?.signal
    ).then((res) => {
      setCompetitionTable(res);
      setSortableColumns(res?.sortable_columns);
      setLoading(false);
      setLoadingTable(false);
    });
  };

  const onChangeViewOptions = (value) =>
    changeViewOptions(competitionHeader, setViewOptions, setHeader, value);

  const handleSortingFunction = (sortingKey, direction) => {
    updateQueryParams({
      orderBy: sortingKey,
      orderDirection: direction,
    });
  };

  const onChangePage = (pageNo) => {
    updateQueryParams({ page: pageNo });
  };

  const onChangeRowsPerPage = (numberOfRows) => {
    setRowsPerPage(numberOfRows);
    updateQueryParams({ limits: numberOfRows });
  };

  const controller = useRef(null);

  // Effect for handling URL changes and initial setup
  useEffect(() => {
    if (isFirstRender.current) isFirstRender.current = false;
    isMounted.current = true;

    if (!competitionId) {
      showWarningSwal("Wrong query");
      setLoading(false);
      setFirstLoaded(true);
      return;
    }

    // Create a new abort controller for this effect
    controller.current = new AbortController();

    // Initial data fetch
    setSelected([]);
    setSelecting([]);
    setLoading(true);
    setLoadingTable(true);

    const queryString = buildQueryString(queryParams);
    getEnhancedCompReport(competitionId, queryString, controller.current.signal)
      .then((res) => {
        setCompetitionTable(res);
        setSortableColumns(res?.sortable_columns);
        setLoading(false);
        setLoadingTable(false);
        setFirstLoaded(true);
      })
      .catch((e) => {
        setLoading(false);
        setLoadingTable(false);
      });

    return () => {
      if (controller.current) {
        controller.current.abort();
      }
      isMounted.current = false;
    };
  }, []);

  // Effect for data fetching when query parameters change
  useEffect(() => {
    if (controller.current && !isFirstRender.current && competitionId) {
      setSelected([]);
      setSelecting([]);
      setLoading(true);
      setLoadingTable(true);

      const queryString = buildQueryString(queryParams);
      getEnhancedCompReport(
        competitionId,
        queryString,
        controller.current.signal
      )
        .then((res) => {
          setCompetitionTable(res);
          setSortableColumns(res?.sortable_columns);
          setLoading(false);
          setLoadingTable(false);
        })
        .catch((e) => {
          setLoading(false);
          setLoadingTable(false);
        });
    }
  }, [queryParams, competitionId]);

  const formatData = (competitionTable) => {
    // Handle both nested (competitionTable.data.data) and flat (competitionTable.data) structures
    const dataArray =
      competitionTable.data?.data || competitionTable.data || [];
    let data = makeSureIsArray(dataArray).map((item) => ({
      ...item,
      marked_manually: isMarkedManually,
      marks_uploaded: item?.points ? "Yes" : "No",
      answers_exists: item?.answers_exists ? "Yes" : "No",
    }));
    // Return structure that HeavyTable expects: { data: [...], current_page: ..., etc. }
    if (competitionTable.data?.data) {
      // Nested structure
      return { ...competitionTable.data, data: data };
    } else {
      // Flat structure
      return { ...competitionTable, data: data };
    }
  };

  const onConfirmEditParticipantRanking = (participantIndex, payload) => {
    showNotification(
      "success",
      "calculating ...",
      enqueueSnackbar,
      closeSnackbar
    );
    patchEditParticipantRanking(participantIndex, payload)
      .then((res) => {
        if (res.error) {
          showNotification(
            "warning",
            res.error,
            enqueueSnackbar,
            closeSnackbar
          );
        } else {
          showNotification(
            "success",
            res.message,
            enqueueSnackbar,
            closeSnackbar,
            setOnEditParticipantRanking(false),
            fetchData()
          );
        }
      })
      .catch((err) => {});
  };

  const ConfirmMarkingPostRequest = () => {
    MarkParticipant(participant.index_no).then((res) => {
      if (res.error) {
        showNotification("warning", res.error, enqueueSnackbar, closeSnackbar);
      } else {
        showNotification(
          "success",
          res.message,
          enqueueSnackbar,
          closeSnackbar,
          cancelConfirmMarking(),
          fetchData()
        );
      }
    });
  };

  const ConfirmMakeAbsentParticipant = () => {
    showNotification(
      "success",
      "setting participant as absent ...",
      enqueueSnackbar,
      closeSnackbar
    );
    MakeAbsentParticipant(participant.index_no).then((res) => {
      if (res.error) {
        showNotification("warning", res.error, enqueueSnackbar, closeSnackbar);
      } else {
        showNotification(
          "success",
          res.message,
          enqueueSnackbar,
          closeSnackbar,
          setAbsentParticipant(false),
          fetchData()
        );
      }
    });
  };

  const downloadFile = ({ data, fileName, fileType }) => {
    const blob = new Blob([data], { type: fileType });

    const a = document.createElement("a");
    a.download = fileName;
    a.href = window.URL.createObjectURL(blob);
    const clickEvt = new MouseEvent("click", {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    a.dispatchEvent(clickEvt);
    a.remove();
  };

  const exportToCsv = (e, data, compname) => {
    // Headers for each column
    let headers = [
      "competition,organization,status,country,grade,school,tuition,index,participant,certificate number,points,award,country_rank,school_rank,global_rank",
    ];
    // Convert users data to a csv
    data.shift();
    let usersCsv = data.reduce((acc, user) => {
      const {
        competition,
        organization,
        status,
        country,
        grade,
        school,
        tuition_centre,
        index_no,
        name,
        certificate_no,
        points,
        award,
        country_rank,
        school_rank,
        global_rank,
      } = user;
      acc.push(
        [
          competition,
          organization,
          status,
          country,
          grade,
          school,
          tuition_centre,
          index_no,
          name,
          certificate_no,
          points,
          award,
          country_rank,
          school_rank,
          global_rank,
        ].join(",")
      );
      return acc;
    }, []);

    downloadFile({
      data: [...headers, ...usersCsv].join("\n"),
      fileName: `${compname}.csv`,
      fileType: "text/csv",
    });
  };

  if (!competitionId) {
    return (
      <Box className="wrapperBox">
        <LoadingBackdrop loading={loading} />
        <NoData height={600} />
      </Box>
    );
  }

  return (
    <Box className="wrapperBox">
      <LoadingBackdrop loading={loading} />
      <Collapse in={viewing}>
        <ViewOption
          viewOptions={viewOptions}
          rowsPerPage={rowsPerPage}
          onChangeViewOptions={onChangeViewOptions}
          onChangeRowsPerPage={onChangeRowsPerPage}
        />
      </Collapse>
      <Collapse in={filtering}>
        <FilterWrapper filters={filters} adjustEndpoint={adjustEndpoint} />
      </Collapse>
      <Grid
        className="firstRowContainer"
        container
        justifyContent="space-between"
      >
        <div className="dashboardAndSelfBtnDiv">
          <ReusableButton
            text="Dashboard"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            width={125}
            br={18}
            to="/dashboard"
            iconType="home"
          />
          <ChevronRightIcon />
          <ReusableButton
            text="Competition Report"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            br={18}
          />
        </div>
        <div className="viewAndFilterBtnDiv">
          <ViewAndFilterButton
            text="View Options"
            state={viewing}
            fontSize={14}
            height={42}
            onClick={handleView}
            marginRight={10}
          />
          <ViewAndFilterButton
            text="Filter"
            state={filtering}
            fontSize={14}
            height={42}
            onClick={handleFilter}
          />
        </div>
      </Grid>
      <Grid className="tableContainer" container>
        <NunitoText
          value="Competition Report"
          fontSize={40}
          fontWeight={700}
          italic
          color="#144A94"
        />
        <Grid
          className="searchAndBtnContainer"
          container
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid>
            <ReusableTextField
              type="search"
              width={500}
              height={60}
              bgColor="#F2F2F2"
              placeholder="Search using keyword"
              state={search}
              setState={onChangeSearch}
              onBlur
            />
            <NunitoText
              value='Search in "Participant Name, Index No, School, Organization"'
              fontSize={20}
              fontWeight={400}
              italic
              color="#144A94"
              align="left"
            />
          </Grid>
          <div className="endBtnDiv">
            <ReusableButton
              text="Export Csv"
              fontSize={15}
              bgColor="#5E75C3"
              height={46}
              marginRight={20}
              iconType="export"
              onClick={(e) => {
                setLoading(true);
                const queryString = buildQueryString(queryParams).replace(
                  "?",
                  "&"
                );
                enhancedGetReport(
                  competitionId,
                  queryString,
                  controller.current?.signal
                ).then((res) => {
                  exportToCsv(
                    e,
                    res,
                    (
                      competitionTable?.data?.data?.[0] ||
                      competitionTable?.data?.[0]
                    )?.competition || "competition"
                  );
                  setLoading(false);
                });
              }}
            />
          </div>
        </Grid>
        {loadingTable && <Loader height={600} />}
        {!loadingTable &&
          competitionTable &&
          competitionTable?.data &&
          makeSureIsArray(
            competitionTable?.data?.data || competitionTable?.data
          )?.length > 0 && (
            <HeavyTable
              handleSortingFunction={handleSortingFunction}
              sortableKeys={sortAbleColumns}
              headers={header}
              list={formatData(competitionTable)}
              title="competition Report"
              rowsPerPage={rowsPerPage}
              setOnEditParticipantRanking={setOnEditParticipantRanking}
              setOnDelete={() => {}}
              onChangePage={onChangePage}
              fixed={[]}
              isSelecting={true}
              selecting={selecting}
              setSelecting={setSelecting}
              selected={selected}
              setSelected={() => {}}
              setId={setParticipant}
              setConfirmMarking={setConfirmMarking}
              setAbsentParticipant={setAbsentParticipant}
            />
          )}
        {!loadingTable &&
          firstLoaded &&
          competitionTable &&
          makeSureIsArray(
            competitionTable?.data?.data || competitionTable?.data
          )?.length === 0 && <NoData height={600} />}
      </Grid>
      <Modal
        open={onEditParticipantRanking}
        onClose={() => cancelEditParticipantRanking()}
      >
        <EditParticipantRankingModal
          participant={participant}
          availableAwards={competitionTable?.filterOptions?.award || []}
          confirmFunction={onConfirmEditParticipantRanking}
          cancelConfirm={cancelEditParticipantRanking}
          awardsEndpoint={
            filters.find((filter) => filter.key === "award").endpoint
          }
        />
      </Modal>
      {competitionTable &&
        (competitionTable?.data || competitionTable?.data?.data) && (
          <Modal open={confirmMarking} onClose={() => cancelConfirmMarking()}>
            <ConfirmModal
              id={participant.index_no}
              selected={[participant]}
              table={
                competitionTable?.data?.data
                  ? competitionTable.data
                  : competitionTable
              }
              action="Mark"
              confirmFunction={ConfirmMarkingPostRequest}
              cancelConfirm={cancelConfirmMarking}
            />
          </Modal>
        )}
      {competitionTable &&
        (competitionTable?.data || competitionTable?.data?.data) && (
          <Modal open={absentParticipant} onClose={() => cancelAbsentStudent()}>
            <ConfirmAbsentParticipant
              id={participant.index_no}
              selected={[participant]}
              table={
                competitionTable?.data?.data
                  ? competitionTable.data
                  : competitionTable
              }
              confirmFunction={ConfirmMakeAbsentParticipant}
              cancelConfirm={cancelAbsentStudent}
            />
          </Modal>
        )}
    </Box>
  );
}
